// Sticky banner + role switcher for demo presentations.
//
// Turns on in two ways:
//   1. A DEMO_MODE deployment (server flag via /app-config), or
//   2. Any tab opened with ?demo=1 — sticky for that tab only (sessionStorage),
//      so you can demo on the normal app in a new tab without a separate site.
//
// The role switcher reuses View-as-Role: picking a role re-fetches that role's
// permissions and re-renders the whole app on the demo data.

import { useEffect, useState } from 'react';
import { appConfigApi } from '../lib/api';
import { usePermissions } from '../contexts/PermissionsContext';
import { rbacApi, type RoleSummary } from '../lib/rbacApi';

const FALLBACK_ROLES: Array<{ key: string; label: string }> = [
  { key: 'ceo', label: 'CEO' },
  { key: 'manager', label: 'Manager' },
  { key: 'hr', label: 'HR' },
  { key: 'recruiter', label: 'Recruiter' },
  { key: 'coordinator', label: 'Coordinator' },
  { key: 'viewer', label: 'Viewer' },
];

// Is this tab a demo tab? ?demo=1 sets a per-tab sticky flag.
function isDemoTab(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('demo') === '1') sessionStorage.setItem('fns_demo', '1');
    return sessionStorage.getItem('fns_demo') === '1';
  } catch { return false; }
}

export default function DemoBanner() {
  const [enabled, setEnabled] = useState(false);
  const [label, setLabel] = useState('Demo mode — sample data');
  const [roles, setRoles] = useState<Array<{ key: string; label: string }>>(FALLBACK_ROLES);
  const { simulatedRole, startSimulation, endSimulation } = usePermissions();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (isDemoTab()) setEnabled(true);
    // Also enable on a server-side DEMO_MODE deployment.
    appConfigApi.get()
      .then((r) => { if (alive && r.data.demo_mode) { setEnabled(true); if (r.data.demo_label) setLabel(r.data.demo_label); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    rbacApi.listRoles()
      .then((rr) => { if (alive && rr.data.roles?.length) setRoles(rr.data.roles.map((x: RoleSummary) => ({ key: x.key, label: x.label }))); })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, [enabled]);

  if (!enabled) return null;

  async function pick(key: string | null) {
    setBusy(true);
    try { if (key) await startSimulation(key); else await endSimulation(); }
    catch { /* admin-only on backend; the demoer is admin so this normally succeeds */ }
    finally { setBusy(false); }
  }

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800, cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.25)', whiteSpace: 'nowrap',
    background: active ? '#0f172a' : 'rgba(255,255,255,0.85)', color: active ? '#fff' : '#1f2937',
  });

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'repeating-linear-gradient(45deg,#f59e0b,#f59e0b 12px,#fbbf24 12px,#fbbf24 24px)',
      borderBottom: '1px solid #d97706', padding: '6px 12px',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <span style={{ color: '#1f2937', fontWeight: 800, fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        🎬 {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#1f2937' }}>View as:</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button disabled={busy} style={chip(!simulatedRole)} onClick={() => pick(null)}>Full access</button>
        {roles.map((r) => (
          <button key={r.key} disabled={busy} style={chip(simulatedRole === r.key)} onClick={() => pick(r.key)}>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}
